// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { View, Text, Button, GSGrid } from "@components";
import type { SpecialContactsType } from "@modules/service/reducer/special-contacts-types/types";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  SpecialContactTypeModal,
  RemoveSpecialContactTypeModal,
} from "@modules/service/components";
import type { State } from "@store/reducers";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  list: SpecialContactsType[],
  current: SpecialContactsType,
  showTypeModal: boolean,
  showDeleteModal: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getList: typeof actionCreators.GetSpecialContactTypes,
  add: typeof actionCreators.AddSpecialContactType,
  update: typeof actionCreators.UpdateSpecialContactType,
  remove: typeof actionCreators.RemoveSpecialContactType,
  setCurrent: typeof actionCreators.SetCurrentSpecialContactType,
  setShowTypeModal: typeof actionCreators.SetShowSpecialContactTypeModal,
  setShowDeleteModal: typeof actionCreators.SetShowDeleteSpecialContactTypeModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class SpecialContactsTypesList extends React.Component<Props> {
  componentDidMount() {
    this.props.getList({
      clientId: this.props.clientId,
    });
  }

  onUpdateSpecialContactType = (formData: Object) => {
    this.props.update({
      clientId: this.props.clientId,
      specialContactTypeId: formData.id,
      data: formData,
    });
  };

  onCreateSpecialContactType = (formData: Object) => {
    this.props.add({
      clientId: this.props.clientId,
      data: formData,
    });
  };

  showTypeModal = (item: ?SpecialContactsType) => () => {
    this.props.setCurrent(item);
    this.props.setShowTypeModal(true);
  };

  closeTypeModal = () => {
    this.props.setCurrent(null);
    this.props.setShowTypeModal(false);
  };

  showDeletePrompt = (item: SpecialContactsType) => () => {
    this.props.setCurrent(item);
    this.props.setShowDeleteModal(true);
  };

  closeDeletePrompt = () => {
    this.props.setCurrent(null);
    this.props.setShowDeleteModal(false);
  };

  handleRemove = () => {
    this.props.remove({
      clientId: this.props.clientId,
      specialContactTypeId: this.props.current.id,
    });
  };

  get columns() {
    return [
      {
        key: "name",
        name: this.props.t("TYPE"),
        align: "left",
      },
      {
        key: "edit",
        name: this.props.t("EDIT"),
        width: 50,
        renderCell: ({ row }: { row: SpecialContactsType }) =>
          row.isEditable ? (
            <span className="material-icons pointer font-size-26" onClick={this.showTypeModal(row)}>
              edit
            </span>
          ) : null,
      },
      {
        key: "delete",
        name: this.props.t("DELETE"),
        width: 50,
        renderCell: ({ row }: { row: SpecialContactsType }) =>
          (row.isEditable && (
            <span
              className="material-icons pointer font-size-26"
              onClick={this.showDeletePrompt(row)}
            >
              delete
            </span>
          )) ||
          "",
      },
    ];
  }

  get data() {
    return this.props.list.filter(item => item.isEditable);
  }

  render() {
    return (
      <View className="gs-container" gap="4">
        <View direction="row" justifyContent="space-between">
          <h6>{this.props.t("CONTACT_TYPES")}</h6>
          <Button materialIcon="add_box" onClick={this.showTypeModal()} size="small">
            {this.props.t("ADD")}
          </Button>
        </View>
        <GSGrid rows={this.data} columns={this.columns} />
        {this.props.showDeleteModal && this.props.current && (
          <RemoveSpecialContactTypeModal
            specialContactType={this.props.current}
            onCancel={this.closeDeletePrompt}
            onSubmit={this.handleRemove}
          />
        )}
        {this.props.showTypeModal && (
          <SpecialContactTypeModal
            data={this.props.current}
            onSubmit={
              this.props.current ? this.onUpdateSpecialContactType : this.onCreateSpecialContactType
            }
            onCancel={this.closeTypeModal}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State, ownProps): PropsFromState => {
  return {
    list: serviceSelectors.getSpecialContactsTypes(state),
    current: serviceSelectors.getCurrentSpecialContactsType(state),
    showTypeModal: serviceSelectors.getShowSpecialContactsTypeModal(state),
    showDeleteModal: serviceSelectors.getShowDeleteSpecialContactsTypeModal(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getList: actionCreators.GetSpecialContactTypes,
  add: actionCreators.AddSpecialContactType,
  update: actionCreators.UpdateSpecialContactType,
  remove: actionCreators.RemoveSpecialContactType,
  setCurrent: actionCreators.SetCurrentSpecialContactType,
  setShowTypeModal: actionCreators.SetShowSpecialContactTypeModal,
  setShowDeleteModal: actionCreators.SetShowDeleteSpecialContactTypeModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(SpecialContactsTypesList));
